import "./style.scss";

import { Skeleton, Tooltip } from "@mui/material";
import type { AgreementValidatorExtended } from "containers/ContractValidationEdit/utils";
import { BodyHighlight, SmallBody, TinyBody } from "helpers/TypographyComponents";
import { formatLocaleDateString } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { AgValidatorStatus } from "store/agreementsStore";
import type { TFetchStatus } from "store/types";
import type { IUser } from "types";

import { useAgreementPageContext } from "containers/ContractValidationEdit/context/StateProvider";

interface IPanelValidators {
    termStatus: TFetchStatus;
    /**
     * Verify if agreement has validators
     */
    hasValidators: boolean;
    validators: AgreementValidatorExtended[];
    /**
     * Agreement creator
     */
    owner: IUser | undefined;
}

const PanelValidators = ({ termStatus, hasValidators, validators, owner }: IPanelValidators) => {
    const [t] = useTranslation();
    const { agreement } = useAgreementPageContext();
    const ownerName = `${owner?.firstname ?? ""} ${owner?.lastname ?? ""}`;

    const getValidationDate = (validator: AgreementValidatorExtended) => {
        if (validator.status === AgValidatorStatus.APPROVE) {
            return t("ContractValidationEdit.ApprovedOn", {
                date: formatLocaleDateString({ date: validator.modified, options: { numeric: true, timeZone: "UTC" } }),
            });
        }
        return "";
    };

    return (
        <>
            {termStatus !== "succeeded" ? (
                <Skeleton sx={{ marginBottom: "1rem" }} />
            ) : (
                <>
                    {hasValidators && (
                        <>
                            <div className="contract-validation-edit-right-panel-validators">
                                <BodyHighlight color="black">
                                    {t(
                                        agreement?.type === "avenant"
                                            ? "ContractValidationEdit.AvenantValidation"
                                            : "ContractValidationEdit.ContractValidation",
                                    )}
                                </BodyHighlight>
                                <>
                                    <div className="contract-validation-edit-right-panel-label" title={ownerName}>
                                        <SmallBody className="validator-name">{ownerName}</SmallBody>
                                        <TinyBody color="grey" style={{ fontWeight: "300" }}>
                                            ({t("ContractValidationEdit.Creator")})
                                        </TinyBody>
                                    </div>
                                    {validators.map((validator) => {
                                        return (
                                            <div
                                                className="contract-validation-edit-right-panel-validator"
                                                key={validator.userId}
                                            >
                                                <div
                                                    className="contract-validation-edit-right-panel-label"
                                                    title={validator.fullName}
                                                >
                                                    <SmallBody className="validator-name">
                                                        {validator.fullName}
                                                    </SmallBody>
                                                    <TinyBody color="grey" style={{ fontWeight: "300" }}>
                                                        ({t("ContractValidationEdit.Validator")})
                                                    </TinyBody>
                                                </div>
                                                <Tooltip title={getValidationDate(validator)}>
                                                    <span className={`status-${validator.status}`}>{`${t(
                                                        `ContractValidationEdit.ValidatorStatus${validator.status}`,
                                                    )}`}</span>
                                                </Tooltip>
                                            </div>
                                        );
                                    })}
                                </>
                            </div>
                            <div className="contract-validation-edit-right-panel-dividers" />
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default PanelValidators;
